import { type MouseEvent as ReactMouseEvent, useState } from 'react'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { useQueryClient } from 'react-query'
import type { MetricDataFilters } from '../../../hooks/analytics'
import { invalidateClimateDataQueries } from '../../../hooks/climateData'
import HazardFilterDialog from '../export/HazardFilterDialog'
import { hasHazardScenarioPreferences } from './useHazardScenarioPreferences'
import type { HazardScenarioPreferencesConfig } from './hazardConfig'

type HazardScenarioPreferenceControlProps = {
  filters: MetricDataFilters | null
  hazardCode?: string | null
  hazardLabel?: string
  config: HazardScenarioPreferencesConfig
  onSave: (filters: MetricDataFilters | null) => void
}

export default function HazardScenarioPreferenceControl({
  filters,
  hazardCode,
  hazardLabel = 'Hazard',
  config,
  onSave,
}: HazardScenarioPreferenceControlProps) {
  const [open, setOpen] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const queryClient = useQueryClient()
  const active = hasHazardScenarioPreferences(filters, config)

  const stopHazardPickerClick = (event: ReactMouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <>
      <Tooltip
        title={
          isApplying
            ? 'Applying scenario preferences'
            : active
            ? 'Edit scenario preferences'
            : 'Configure scenario preferences'
        }
      >
        <IconButton
          size="small"
          aria-label="Configure scenario preferences"
          sx={{
            width: 28,
            height: 28,
            color: active ? 'primary.main' : 'text.secondary',
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            backgroundColor: active ? 'action.selected' : 'transparent',
            '&:hover': {
              backgroundColor: active ? 'action.selected' : 'action.hover',
            },
          }}
          onMouseDown={stopHazardPickerClick}
          onClick={event => {
            stopHazardPickerClick(event)
            setOpen(true)
          }}
        >
          {isApplying ? (
            <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
          ) : (
            <TuneOutlinedIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>

      <HazardFilterDialog
        open={open}
        hazardLabel={hazardLabel}
        dialogTitle={`${hazardLabel} scenario preferences`}
        initialFilters={filters}
        editorConfig={config}
        onClose={() => setOpen(false)}
        onSave={nextFilters => {
          onSave(nextFilters)
          setIsApplying(true)
          invalidateClimateDataQueries(queryClient, { hazardCode })
            .catch(() => undefined)
            .finally(() => setIsApplying(false))
        }}
      />
    </>
  )
}
